import { useTranslation } from 'react-i18next';
import type { AiBriefInstructionStatus } from '@apis/aiAgent';
import { formatBriefDate } from '../MorningBrief/briefDay';
import styles from './instructionStatus.module.css';

/**
 * What a reader needs to know about their standing instruction, one line each.
 *
 * A STANDING INSTRUCTION MUST NEVER FAIL SILENTLY. Each line here is a way it
 * used to: asking for something the brief cannot show, being about a section the
 * reader muted, naming a floor the site does not have, starting later, or having
 * ended. The same lines are shown before saving (the settings dialog's check)
 * and every morning after (above the insights), from the same server rules — so
 * the dialog cannot promise what the brief then ignores.
 *
 * Nothing to say renders nothing: the absence of a line is the all-clear.
 */
interface IInstructionLinesProps {
  status: AiBriefInstructionStatus;
}

export function InstructionLines({ status }: IInstructionLinesProps) {
  const { t, i18n } = useTranslation();
  const day = (date: string) => formatBriefDate(date, i18n.language);

  const lines = [
    ...(status.unsupported.length === 0
      ? []
      : [t('aiAgent.instruction.unsupported', { items: status.unsupported.join(', ') })]),
    ...(status.mutedConflicts.length === 0
      ? []
      : [
          t('aiAgent.instruction.mutedConflict', {
            sections: status.mutedConflicts.map((key) => t(`aiAgent.brief.sections.${key}.title`)).join(', '),
          }),
        ]),
    ...(status.unknownFloor === null ? [] : [t('aiAgent.instruction.unknownFloor', { floor: status.unknownFloor })]),
    ...(status.expiredOn === null ? [] : [t('aiAgent.instruction.expired', { date: day(status.expiredOn) })]),
    ...(status.startsOn === null ? [] : [t('aiAgent.instruction.startsOn', { date: day(status.startsOn) })]),
  ];

  if (lines.length === 0) return null;
  return (
    <ul className={styles.lines}>
      {lines.map((line) => (
        <li key={line} className={styles.line}>
          {line}
        </li>
      ))}
    </ul>
  );
}
